import React from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { Link } from '@tanstack/react-router';
import { formatBoolean } from '@utils/formats.ts';
import { DayOfWeek } from '@api/generated/search-service';
import { useOrgPortal } from '@hooks/useOrgPortal.ts';

interface ProductInfoProps {
  details: {
    ecid: string;
    supplier: string;
    minAmount: number;
    onlyMultiple: boolean;
    catalogNumber: string;
    catalogName: string;
    description: string;
    deliveryDays: Array<DayOfWeek>;
    /** Aukce, ze které vybraná cena pochází. Chybí u cen mimo aukci (ruční, katalogové). */
    auction?: string | null;
    /** Id té aukce — když je, udělá se z názvu proklik na její detail. */
    auctionId?: number | null;
    /** Poznámka dodavatele ke katalogové položce. */
    supplierNote?: string | null;
  };
  /**
   * Když je true, schovat všechna pole vázaná na dodavatele (supplier, deliveryDays,
   * catalogNumber, catalogName, description). Použito v empty-state pro datum dodání,
   * kde N/A pole vypadají divně.
   */
  compact?: boolean;
}

/** Pořadí dnů pro zobrazení — API vrací set, chceme stabilní Po–Ne. */
const DAY_ORDER: DayOfWeek[] = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

/** Z enumu API (MONDAY, ...) udela prelozene nazvy dnu, serazene Po–Ne. */
const formatDeliveryDays = (days: Array<DayOfWeek> | undefined, t: TFunction): string =>
  [...(days ?? [])]
    .sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b))
    .map(day => t(`common.${String(day).toLowerCase()}`))
    .join(', ');

const ProductInfo: React.FC<ProductInfoProps> = ({ details, compact = false }) => {
  const { t } = useTranslation('client');
  const { orgId, portalId } = useOrgPortal();
  return (
    <div className="w-full flex flex-col justify-start items-start gap-6">
      {/* Základní informace */}
      <div className="w-full flex flex-wrap justify-start items-start gap-4">
        <div className="h-[50px] p-2 bg-gray-50 rounded inline-flex flex-col justify-start items-center gap-2">
          <div className="text-center justify-center text-zinc-900 text-[13px] font-medium font-['Borna'] leading-[13px]">{t('productDetail.minOrder')}</div>
          <div className="text-center justify-center text-zinc-900 text-[13px] font-medium font-['Borna'] leading-[13px]">{details.minAmount}</div>
        </div>
        <div className="h-[50px] p-2 bg-gray-50 rounded inline-flex flex-col justify-start items-center gap-2">
          <div className="text-center justify-center text-zinc-900 text-[13px] font-medium font-['Borna'] leading-[13px]">{t('productDetail.multiples')}</div>
          <div className="text-center justify-center text-zinc-900 text-[13px] font-medium font-['Borna'] leading-[13px]">{formatBoolean(details.onlyMultiple)}</div>
        </div>
        {/*<AllergensOverlay allergens={basicInfo.allergens} allergenList={allergenList} />*/}
      </div>

      {/* Detailní informace o produktu */}
      <div className="w-full flex flex-col justify-start items-start gap-2">
        <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-1 sm:gap-3">
          <div className="text-zinc-500 text-sm font-medium font-['Borna'] leading-tight">{t('productDetail.ecid')}</div>
          <div className="text-zinc-900 text-sm font-medium font-['Borna'] leading-tight">{details.ecid}</div>

          {!compact && (
            <>
              <div className="text-zinc-500 text-sm font-medium font-['Borna'] leading-tight">{t('productDetail.supplierLabel')}</div>
              <div className="text-zinc-900 text-sm font-medium font-['Borna'] leading-tight">{details.supplier}</div>
              <div className="text-zinc-500 text-sm font-medium font-['Borna'] leading-tight">{t('productDetail.deliveryDays')}</div>
              <div className="text-zinc-900 text-sm font-medium font-['Borna'] leading-tight">{formatDeliveryDays(details.deliveryDays, t)}</div>

              <div className="text-zinc-500 text-sm font-medium font-['Borna'] leading-tight">{t('productDetail.catalogNumber')}</div>
              <div className="text-zinc-900 text-sm font-medium font-['Borna'] leading-tight">{details.catalogNumber}</div>

              {/* Jen kdyz cena z aukce opravdu vzesla — prazdny radek s N/A nikomu nic nerekne. */}
              {details.auction && (
                <>
                  <div className="text-zinc-500 text-sm font-medium font-['Borna'] leading-tight">{t('productDetail.auction')}</div>
                  {/* Proklik jen kdyz aukci umime adresovat. Odchod na jinou routu zahodi
                      `productId` v query, takze se modal produktu zavre sam. */}
                  {details.auctionId ? (
                    <Link
                      to="/c/$orgId/$portalId/auctions/$auctionId"
                      params={{ orgId, portalId, auctionId: details.auctionId }}
                      search={{ tab: 'prices', page: 1, size: 20 }}
                      className="text-primary text-sm font-medium font-['Borna'] leading-tight hover:underline"
                    >
                      {details.auction}
                    </Link>
                  ) : (
                    <div className="text-zinc-900 text-sm font-medium font-['Borna'] leading-tight">{details.auction}</div>
                  )}
                </>
              )}
            </>
          )}
        </div>
        {!compact && (
          <div className={'w-full flex flex-col justify-start items-start gap-3'}>
            <div className="w-full flex flex-col justify-start items-start gap-1 mt-4">
              <div className="w-full sm:w-[130px] text-zinc-500 text-sm font-medium font-['Borna'] leading-tight">{t('productDetail.catalogName')}</div>
              <div className="w-full text-zinc-900 text-sm font-medium font-['Borna'] leading-tight">{details.catalogName}</div>
            </div>

            <div className="w-full flex flex-col justify-start items-start gap-1">
              <div className="w-full sm:w-[130px] text-zinc-500 text-sm font-medium font-['Borna'] leading-tight">{t('productDetail.productDescription')}</div>
              <div className="w-full text-zinc-900 text-sm font-medium font-['Borna'] leading-tight">{details.description}</div>
            </div>

            {details.supplierNote && (
              <div className="w-full flex flex-col justify-start items-start gap-1">
                <div className="w-full sm:w-[130px] text-zinc-500 text-sm font-medium font-['Borna'] leading-tight">{t('productDetail.supplierNote')}</div>
                {/* Volny text od dodavatele — radky si pise sam, tak se zachovavaji. */}
                <div className="w-full whitespace-pre-line text-zinc-900 text-sm font-medium font-['Borna'] leading-tight">{details.supplierNote}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductInfo;
